import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Input, { BareInput } from "components/DesignSystem/Input/Input";
import { Pencil20 } from "components/icons/Pencil20";
import { Trash20 } from "components/icons/Trash20";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useFormikContext } from "formik";
import { useState } from "react";
import { NumericFormat } from "react-number-format";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import {
  EntityPerson,
  EntityPersonInput,
  EntityPersonRole,
  useAddEntityPersonMutation,
  useDeleteEntityPersonMutation,
  useUpdateEntityPersonMutation,
} from "v2/store/onboardingEntityApi";
import { getGroupedCountryOptionsByUuid } from "utils/stateRequired";
import { useToast } from "hooks/useToast";
import { readError } from "v2/utils/readError";
import * as Yup from "yup";

type PeopleSectionProps = {
  people: EntityPerson[];
  entityId: string;
  groupId: string;
};

type PersonFormValues = {
  name: string;
  countryId: string;
  countryName: string;
  roles: EntityPersonRole[];
  ownership: string;
};

const FOUNDER_ROLE: EntityPersonRole = "Founder";

const OWNERSHIP_REQUIRED = "Ownership % is required for a founder";
const OWNERSHIP_POSITIVE = "Ownership % must be greater than 0";

const ROLE_OPTIONS: EntityPersonRole[] = [
  "Director",
  FOUNDER_ROLE,
  "Officer",
  "Others",
];

const EMPTY_FORM: PersonFormValues = {
  name: "",
  countryId: "",
  countryName: "",
  roles: [],
  ownership: "",
};

const personSchema = Yup.object({
  name: Yup.string().required("Full name is required"),
  countryId: Yup.string().required("Country of residence is required"),
  roles: Yup.array().min(1, "At least one role is required"),
  ownership: Yup.string().when("roles", {
    is: (roles?: EntityPersonRole[]) => Boolean(roles?.includes(FOUNDER_ROLE)),
    then: Yup.string()
      .required(OWNERSHIP_REQUIRED)
      .test(
        "ownership-positive",
        OWNERSHIP_POSITIVE,
        (value) => Number(value) > 0
      ),
  }),
});

const PersonRow = ({
  person,
  onEdit,
  onRemove,
}: {
  person: EntityPerson;
  onEdit: () => void;
  onRemove: () => void;
}) => {
  const roleSummary = person.roles.join(", ");
  const ownershipText = person.ownership ? ` (${person.ownership}%)` : "";
  const roleText = `${roleSummary}${ownershipText}`;
  const taxResidencyPrefix = person.country
    ? `Tax residency: ${person.country} | `
    : "";

  return (
    <div className="t-flex t-items-center t-justify-between">
      <div>
        <div className="t-text-subtext t-font-normal t-text-text-60">
          {person.name}
        </div>
        <div className="t-text-body-sm t-text-text-30">
          {taxResidencyPrefix}Role: {roleText}
        </div>
      </div>
      <div className="t-flex t-items-center t-gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="all:unset t-flex t-cursor-pointer t-p-1 t-text-text-30 hover:t-text-purple"
          title="Edit"
        >
          <Pencil20 className="t-size-4" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="all:unset t-flex t-cursor-pointer t-p-1 t-text-text-30 hover:t-text-red"
          title="Remove"
        >
          <Trash20 className="t-size-4" />
        </button>
      </div>
    </div>
  );
};

const PersonFormFields = ({
  people,
  editingId,
  onCancel,
}: {
  people: EntityPerson[];
  editingId: string | null;
  onCancel: () => void;
}) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } =
    useFormikContext<PersonFormValues>();
  const { data: countries = [] } = useGetCountriesDataQuery();

  const countryOptions = getGroupedCountryOptionsByUuid(countries);

  const isShareholder = values.roles.includes(FOUNDER_ROLE);

  const totalExistingOwnership = people.reduce((sum, person) => {
    if (person.id === editingId) return sum;
    if (!person.roles.includes(FOUNDER_ROLE) || !person.ownership) return sum;
    return sum + parseFloat(person.ownership);
  }, 0);

  const remainingOwnership = 100 - totalExistingOwnership;
  const ownershipError = touched.ownership ? errors.ownership : undefined;

  const handleCountryChange = (
    option: { value: string; label: string } | null
  ) => {
    setFieldValue("countryId", option?.value ?? "");
    setFieldValue("countryName", option?.label ?? "");
  };

  const handleRolesChange = (option: { value: string }[] | null) => {
    const selected = option ?? [];
    setFieldValue(
      "roles",
      selected.map((item) => item.value as EntityPersonRole)
    );
  };

  const isOwnershipAllowed = ({ floatValue }: { floatValue?: number }) =>
    floatValue === undefined || floatValue <= remainingOwnership;

  const handleOwnershipChange = ({ value }: { value: string }) =>
    setFieldValue("ownership", value);

  const handleOwnershipBlur = () => setFieldTouched("ownership", true);

  return (
    <div className="t-flex t-flex-col t-gap-4 t-rounded-lg t-shadow-light-30 t-p-4">
      <div className="t-grid t-grid-cols-2 sm:t-grid-cols-1 t-gap-4">
        <Input
          withForm
          name="name"
          required
          label="Full name"
          placeholder="e.g. Alex Chen"
        />
        <Combobox
          menuPlacement="auto"
          withForm
          required
          name="countryId"
          label="Country of residence"
          placeholder="Select country"
          options={countryOptions}
          value={
            values.countryId
              ? { value: values.countryId, label: values.countryName }
              : null
          }
          onChange={(option) =>
            handleCountryChange(
              option as { value: string; label: string } | null
            )
          }
          menuPortalTarget={document.body}
        />
        <Combobox
          menuPlacement="auto"
          withForm
          isMulti
          multiValueShowLimit={1}
          name="roles"
          required
          label="Role"
          placeholder="Select role(s)"
          options={ROLE_OPTIONS.map((role) => ({ value: role, label: role }))}
          value={values.roles.map((role) => ({ value: role, label: role }))}
          onChange={(option) =>
            handleRolesChange(option as { value: string }[] | null)
          }
          menuPortalTarget={document.body}
        />
      </div>
      {isShareholder && (
        <div className="t-max-w-[160px]">
          <NumericFormat
            customInput={BareInput}
            label="Ownership %"
            placeholder="e.g. 25"
            suffix="%"
            decimalScale={2}
            allowNegative={false}
            isAllowed={isOwnershipAllowed}
            value={values.ownership}
            onValueChange={handleOwnershipChange}
            onBlur={handleOwnershipBlur}
            variant={ownershipError ? "error" : undefined}
            helperText={ownershipError}
            required
          />
        </div>
      )}
      <div className="t-flex t-gap-2">
        <Button type="submit" size="small" customType="primary_outlined">
          Save
        </Button>
        <Button
          type="button"
          size="small"
          customType="secondary"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export const PeopleSection = ({
  people,
  entityId,
  groupId,
}: PeopleSectionProps) => {
  const { alertToast } = useToast();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [initialValues, setInitialValues] =
    useState<PersonFormValues>(EMPTY_FORM);

  const [addEntityPerson] = useAddEntityPersonMutation();
  const [updateEntityPerson] = useUpdateEntityPersonMutation();
  const [deleteEntityPerson] = useDeleteEntityPersonMutation();

  const openEditForm = (person: EntityPerson) => {
    setEditingId(person.id);
    setInitialValues({
      name: person.name,
      countryId: person.countryId ?? "",
      countryName: person.country ?? "",
      roles: person.roles,
      ownership: person.ownership ?? "",
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setInitialValues(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async (values: PersonFormValues) => {
    const isShareholder = values.roles.includes("Founder");
    const person: EntityPersonInput = {
      name: values.name,
      countryName: values.countryName,
      countryId: values.countryId || undefined,
      roles: values.roles,
      ownership: isShareholder ? values.ownership : undefined,
    };

    try {
      if (editingId) {
        await updateEntityPerson({
          entityId,
          groupId,
          personId: editingId,
          person,
        }).unwrap();
      } else {
        await addEntityPerson({ entityId, groupId, person }).unwrap();
      }
      closeForm();
    } catch (error) {
      alertToast({ message: readError(error) });
    }
  };

  const handleRemove = async (personId: string) => {
    try {
      await deleteEntityPerson({ entityId, groupId, personId }).unwrap();
    } catch (error) {
      alertToast({ message: readError(error) });
    }
  };

  return (
    <div className="t-flex t-flex-col t-gap-3">
      {people.map((person) => (
        <PersonRow
          key={person.id}
          person={person}
          onEdit={() => openEditForm(person)}
          onRemove={() => void handleRemove(person.id)}
        />
      ))}

      {showForm ? (
        <FormikForm
          initialValues={initialValues}
          validationSchema={personSchema}
          enableReinitialize
          onSubmit={handleSave}
        >
          <PersonFormFields
            people={people}
            editingId={editingId}
            onCancel={closeForm}
          />
        </FormikForm>
      ) : (
        <div>
          <Button
            type="button"
            customType="secondary"
            size="small"
            onClick={() => setShowForm(true)}
          >
            + Add person
          </Button>
        </div>
      )}
    </div>
  );
};
